/**
 * Pure functions for building section hierarchies from flat section lists.
 *
 * SRP: This module ONLY handles tree building. No UI, no state, no I/O.
 * Testable in isolation — every function is pure.
 *
 * Hierarchy is derived from section NUMBERS (e.g., "2.1" is a child of "2"),
 * not from any "level" field that may be unreliable.
 */

import type { SectionSummary } from "../types/blocks";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Depth of a section in the hierarchy (0 = top level, 1 = "X.Y", etc.) */
export function getDepth(sectionNumber: string): number {
  return sectionNumber.split(".").length - 1;
}

/** Parent section number for a given section, or null if top-level */
export function getParentNumber(sectionNumber: string): string | null {
  if (!sectionNumber.includes(".")) return null;
  return sectionNumber.split(".").slice(0, -1).join(".");
}

/** True if `child` is a direct child of `parent` (one level deeper) */
export function isDirectChild(child: string, parent: string): boolean {
  if (child === parent) return false;
  if (!child.startsWith(`${parent}.`)) return false;
  return getDepth(child) === getDepth(parent) + 1;
}

// ---------------------------------------------------------------------------
// Tree building from a flat list
// ---------------------------------------------------------------------------

/**
 * Determine if a section is a top-level node in the given list.
 * A section is top-level if:
 *   - Its number contains no dot, OR
 *   - Its parent (by number) does not exist in the list
 */
export function isTopLevel(section: SectionSummary, allSections: SectionSummary[]): boolean {
  const parent = getParentNumber(section.sectionNumber);
  if (parent === null) return true;
  return !allSections.some((s) => s.sectionNumber === parent);
}

/**
 * Get the direct children of a section (one level deeper, same prefix).
 */
export function getChildren(parentNumber: string, allSections: SectionSummary[]): SectionSummary[] {
  return allSections.filter((s) => isDirectChild(s.sectionNumber, parentNumber));
}

/**
 * Get all top-level sections from a flat list.
 */
export function getTopLevelSections(allSections: SectionSummary[]): SectionSummary[] {
  return allSections.filter((s) => isTopLevel(s, allSections));
}

// ---------------------------------------------------------------------------
// Deduplication
// ---------------------------------------------------------------------------

/**
 * Dedupe sections by section number.
 *
 * When multiple entries have the same section number (e.g., from a Word
 * document with duplicate insertions), keep the entry with the most content.
 */
export function dedupeByNumber<T extends { sectionNumber: string; contentLength?: number }>(sections: T[]): T[] {
  const byNumber = new Map<string, T>();
  for (const s of sections) {
    const existing = byNumber.get(s.sectionNumber);
    if (!existing || (s.contentLength || 0) > (existing.contentLength || 0)) {
      byNumber.set(s.sectionNumber, s);
    }
  }
  return Array.from(byNumber.values());
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/**
 * Sort sections in document order (by section number).
 * "TITLE" sorts first, then numeric ordering: "1", "2", "2.1", "2.2", "10", etc.
 */
export function sortByDocumentOrder<T extends { sectionNumber: string }>(sections: T[]): T[] {
  return [...sections].sort((a, b) => {
    if (a.sectionNumber === "TITLE") return -1;
    if (b.sectionNumber === "TITLE") return 1;
    const aParts = a.sectionNumber.split(".").map(Number);
    const bParts = b.sectionNumber.split(".").map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const av = aParts[i] ?? 0;
      const bv = bParts[i] ?? 0;
      if (av !== bv) return av - bv;
    }
    return 0;
  });
}
